import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Input,
  DatePicker,
  InputNumber,
  Spin,
  Dropdown,
  Tag,
  Space,
} from 'antd'
import { FormInstance } from 'antd/lib/form'
import moment from 'moment'
import { DownOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { PAGE_EXPAND_NAME_ENUM_LIST } from '../../api/MaterialApi'
import Style from './index.module.scss'

export interface ExpandAttrFieldItem {
  id: string
  name: string
  valueType?: string // 值类型：'1' 枚举，'2' 文本，'3' 数字，'4' 日期
  multiple?: string // 是否多选：'0' 单选，'1' 多选
  enumValueList?: string[] // 选中的枚举值列表
  allEnumValueList?: string[] // 所有枚举值列表
  value?: string // 字段值
  required?: boolean // 是否必填：false 不必填，true 必填
  platformAbbreviationName?: string // 平台要求（可选）
}

// 属性值列表项接口
interface AttributeValueItem {
  id: string
  name: string
  value: string
  code?: string
}

// 获取属性值列表的 API 函数
const getAttributeValueList = async (params: {
  fieldNameId: string // 属性项 id
  pageNo: number // 页码
  pageSize: number // 每页数量
  keyword?: string // 搜索关键词
}): Promise<{
  current: number
  total: number
  data: AttributeValueItem[]
}> => {
  const res = await PAGE_EXPAND_NAME_ENUM_LIST({
    fieldNameId: params.fieldNameId,
    pageNo: params.pageNo,
    pageSize: params.pageSize,
    value: params.keyword || '',
  })
  if (res.code === 0 && res.data) {
    return {
      current: res.data.pageNo || params.pageNo,
      total: res.data.totalCount || 0,
      data: (res.data.content || []).map((item: any) => ({
        id: item.id,
        name: item.value, // API 返回的 value 字段映射为 name
        value: item.value,
        code: item.id,
      })),
    }
  }
  return {
    current: params.pageNo,
    total: 0,
    data: [],
  }
}

interface RenderFormControlProps {
  item: ExpandAttrFieldItem
  placeholder?: string
  allReadOnly?: boolean
  readOnlyComponent?: React.ReactNode // 只读状态下的组件
  form?: FormInstance // 表单实例，用于获取校验状态
  fieldKey?: 'id' | 'name'
}

// 支持滚动加载和搜索的 Select 组件
// 约定：枚举字段后端 enumValueList 永远是数组，前端表单值也统一使用 string[]（单选长度为 0/1）
interface ScrollLoadSelectProps {
  item: ExpandAttrFieldItem
  placeholder?: string
  isMultiple: boolean
  value?: string[] // 表单值，统一使用数组
  onChange?: (value: string[] | undefined) => void // 表单值变化回调
  status?: 'error' | 'warning' // 表单校验状态
}

const ScrollLoadSelect: React.FC<ScrollLoadSelectProps> = ({
  item,
  placeholder = '请输入',
  isMultiple,
  value,
  onChange,
  status,
}) => {
  const [dataSource, setDataSource] = useState<AttributeValueItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [hasMore, setHasMore] = useState(true)
  const [visible, setVisible] = useState(false)
  const currentPageRef = useRef(1)
  const totalRef = useRef(0)
  const loadingRef = useRef(false)
  const searchTimerRef = useRef<NodeJS.Timeout>()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const pageSize = 10 // 每页数量

  const fieldId = item.id
  // 用于标记是否已经初始化过（区分初始值和用户清空后的值）
  const valueInitializedRef = useRef(false)

  // 选中的值从表单 value 获取，如果没有则从 item.enumValueList 获取（初始值）
  const selectedValues = React.useMemo(() => {
    if (value !== undefined) {
      // 只要表单里已经有值（包括 []），认为已经初始化过
      valueInitializedRef.current = true
      return value
    }
    // 如果已经初始化过，说明用户主动清空了，返回空数组
    // 否则返回初始值（来自字段配置的 enumValueList）
    if (valueInitializedRef.current) {
      return []
    }
    return item.enumValueList || []
  }, [value, item.enumValueList])

  // 加载数据
  const loadData = useCallback(
    async (page = 1, keyword = '', append = false) => {
      if (!fieldId || loadingRef.current) return

      loadingRef.current = true
      setLoading(true)

      try {
        const result = await getAttributeValueList({
          fieldNameId: fieldId,
          pageNo: page,
          pageSize: pageSize,
          keyword: keyword,
        })

        if (result.data) {
          if (append) {
            setDataSource((prev) => [...prev, ...result.data])
          } else {
            setDataSource(result.data)
          }

          totalRef.current = result.total || 0
          currentPageRef.current = result.current || page
          setHasMore((result.current || page) * pageSize < (result.total || 0))
        }
      } catch (error) {
        // 加载数据失败，静默处理
        // eslint-disable-next-line no-console
        console.error('加载数据失败:', error)
      } finally {
        setLoading(false)
        loadingRef.current = false
      }
    },
    [fieldId],
  )

  // 初始化加载
  const initializedRef = useRef(false)
  useEffect(() => {
    if (visible && !initializedRef.current) {
      if (fieldId) {
        // 如果有 fieldId，从 API 加载数据
        loadData(1, '', false)
        initializedRef.current = true
      } else if (item.allEnumValueList && item.allEnumValueList.length > 0) {
        // 如果没有 fieldId 但有静态枚举列表，使用静态数据
        const staticData: AttributeValueItem[] = item.allEnumValueList.map(
          (val: string) => ({
            id: val,
            name: val,
            value: val,
          }),
        )
        setDataSource(staticData)
        setHasMore(false)
        initializedRef.current = true
      }
    }
    if (!visible) {
      // 关闭下拉框时重置初始化状态
      initializedRef.current = false
    }
  }, [fieldId, visible, item.allEnumValueList, loadData])

  // 打开下拉框时加载数据
  const handleVisibleChange = (open: boolean) => {
    setVisible(open)
  }

  // 处理搜索
  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value)
      currentPageRef.current = 1

      // 防抖处理
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }

      searchTimerRef.current = setTimeout(() => {
        if (fieldId) {
          // 从 API 搜索
          loadData(1, value, false)
        } else if (item.allEnumValueList) {
          // 本地过滤静态枚举列表
          const filtered = item.allEnumValueList.filter((val: string) =>
            val.toLowerCase().includes(value.toLowerCase()),
          )
          const staticData: AttributeValueItem[] = filtered.map(
            (val: string) => ({
              id: val,
              name: val,
              value: val,
            }),
          )
          setDataSource(staticData)
        }
      }, 300)
    },
    [fieldId, loadData],
  )

  // 处理滚动加载
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { target } = e
      const scrollElement = target as HTMLElement
      const scrollTop = scrollElement.scrollTop
      const scrollHeight = scrollElement.scrollHeight
      const clientHeight = scrollElement.clientHeight

      // 滚动到底部附近时加载更多（仅在有 fieldId 时，静态数据不需要分页）
      if (
        scrollHeight - scrollTop - clientHeight < 50 &&
        hasMore &&
        !loading &&
        fieldId
      ) {
        loadData(currentPageRef.current + 1, searchValue, true)
      }
    },
    [hasMore, loading, fieldId, searchValue, loadData],
  )

  // 点击行选择
  const handleClickRow = useCallback(
    (record: AttributeValueItem) => {
      const recordValue = record.name

      if (isMultiple) {
        // 多选：切换选中状态
        const newSelected = selectedValues.includes(recordValue)
          ? selectedValues.filter((v) => v !== recordValue)
          : [...selectedValues, recordValue]

        onChange?.(newSelected)
      } else {
        // 单选：保存为长度为 1 的数组并关闭下拉框
        onChange?.([recordValue])
        setVisible(false)
      }
    },
    [isMultiple, selectedValues, onChange],
  )

  // 判断是否选中
  const isSelected = useCallback(
    (value: string) => {
      return selectedValues.includes(value)
    },
    [selectedValues],
  )

  // 清理定时器
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  // 渲染选中值显示
  const renderSelectedDisplay = () => {
    if (isMultiple) {
      if (selectedValues.length === 0) {
        return <span style={{ color: '#bfbfbf' }}>{placeholder}</span>
      }
      return (
        <Space wrap size={[4, 4]}>
          {selectedValues.map((val) => (
            <Tag
              key={val}
              closable
              onClose={(e) => {
                e.stopPropagation()
                const newSelected = selectedValues.filter((v) => v !== val)
                onChange?.(newSelected)
              }}
              style={{
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                position: 'relative', // 添加相对定位
              }}
            >
              <span
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={val}
              >
                {val}
              </span>
            </Tag>
          ))}
        </Space>
      )
    } else {
      return selectedValues.length > 0 ? selectedValues[0] : placeholder
    }
  }

  // 下拉面板内容
  const dropdownContent = (
    <div
      style={{
        width: '100%',
        minWidth: '200px',
        maxHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <span style={{ fontSize: '12px', margin: '8px 0 0 8px' }}>
        {isMultiple ? `已选：${selectedValues?.length}个` : ''}
      </span>
      {/* 搜索框 */}
      <div
        className={Style['dropDown-search']}
        style={{
          padding: '8px',
          borderBottom: '1px solid #d9d9d9',
        }}
      >
        <Input
          placeholder="请输入搜索关键词"
          size="small"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
        />
      </div>

      {/* 列表内容 */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: '200px',
        }}
      >
        {loading && dataSource.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="small" />
          </div>
        ) : dataSource.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            暂无数据
          </div>
        ) : (
          dataSource.map((record) => {
            const selected = isSelected(record.name)
            return (
              <div
                key={record.value || record.name}
                onClick={() => handleClickRow(record)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: selected ? '#f0f7ff' : 'transparent',
                  borderLeft: selected
                    ? '3px solid #1890ff'
                    : '3px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (!selected) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selected) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <div style={{ fontSize: '14px' }}>{record.name}</div>
              </div>
            )
          })
        )}
        {loading && dataSource.length > 0 && (
          <div style={{ textAlign: 'center', padding: '8px' }}>
            <Spin size="small" />
          </div>
        )}
      </div>
    </div>
  )

  return (
    <Dropdown
      overlay={dropdownContent}
      trigger={['click']}
      visible={visible}
      onVisibleChange={handleVisibleChange}
      getPopupContainer={(triggerNode) =>
        triggerNode.parentElement || document.body
      }
    >
      <div
        style={{
          padding: '2px 4px',
          border:
            status === 'error' ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
          cursor: 'pointer',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff',
          position: 'relative',
        }}
      >
        <div
          style={{
            maxHeight: '100px',
            overflow: 'auto',
          }}
          onClick={() => {
            setVisible(!visible)
            setSearchValue('')
          }}
        >
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {renderSelectedDisplay()}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            position: 'absolute',
            right: 4,
            top: 4,
          }}
        >
          {selectedValues.length > 0 && (
            <CloseCircleOutlined
              onClick={(e) => {
                e.stopPropagation()
                // 清空时统一置为空数组，便于必填校验依赖长度判断
                onChange?.([])
              }}
              style={{ color: '#999', fontSize: '12px' }}
            />
          )}
          <DownOutlined
            style={{
              color: '#999',
              fontSize: '8px',
              transform: visible ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </div>
      </div>
    </Dropdown>
  )
}

// 渲染不同类型的表单控件
export const renderFormControl = ({
  item,
  placeholder = '请输入',
  form,
  fieldKey = 'id',
}: RenderFormControlProps) => {
  const valueType = item.valueType || '2' // 默认为文本类型

  switch (valueType) {
    case '1': {
      // 枚举类型
      const isMultiple = item.multiple === '1'
      // 使用包装组件来接收Form.Item传递的value和onChange，并通过form获取校验状态
      const WrappedScrollLoadSelect: React.FC<{
        value?: string | string[]
        onChange?: (value: string[] | undefined) => void
      }> = (props) => {
        const { value, onChange } = props

        // 统一把表单值规范成数组（兼容历史上单选存 string 的场景）
        const normalizedValue = React.useMemo<string[] | undefined>(() => {
          if (value === undefined) return undefined
          return Array.isArray(value) ? value : [String(value)]
        }, [value])

        // 如果当前值不是数组，则同步一次到表单中，保证校验和提交时都是数组
        React.useEffect(() => {
          if (value !== undefined && !Array.isArray(value)) {
            onChange?.(normalizedValue || [])
          }
        }, [value, normalizedValue, onChange])
        // 在渲染时直接获取字段的校验错误状态
        // Form.Item会在字段值变化时重新渲染，所以这里会实时获取最新的校验状态
        // 使用 item.id 或 item.name 作为字段名（根据实际使用情况）
        // const fieldName = item.id || item.name
        const field = fieldKey === 'id' ? item.id : item.name
        const fieldError = form?.getFieldError?.(field)
        const hasError = fieldError && fieldError.length > 0
        const status = hasError ? 'error' : undefined

        return (
          <ScrollLoadSelect
            item={item}
            placeholder={placeholder}
            isMultiple={isMultiple}
            value={normalizedValue}
            onChange={onChange}
            status={status}
          />
        )
      }
      WrappedScrollLoadSelect.displayName = 'WrappedScrollLoadSelect'
      return <WrappedScrollLoadSelect />
    }

    case '2': // 文本类型
      return <Input placeholder={placeholder} maxLength={40} />

    case '3': // 数字类型
      return (
        <InputNumber
          placeholder={placeholder}
          style={{ width: '100%' }}
          min={-999999999 as number}
          max={999999999 as number}
          precision={0}
        />
      )

    case '4': // 日期类型
      return (
        <DatePicker
          placeholder={placeholder}
          style={{ width: '100%' }}
          format="YYYY-MM-DD"
          allowClear
          disabledDate={() => {
            return false
          }}
        />
      )

    default:
      return <Input placeholder={placeholder} />
  }
}

// 获取验证规则
export const getFieldRules = (item: ExpandAttrFieldItem) => {
  const valueType = item.valueType || '2'
  const isTextType = valueType === '2'
  const isRequired = item.required

  const rules: Array<{
    required?: boolean
    message?: string
    type?: 'string' | 'array' | 'number'
    whitespace?: boolean
  }> = []

  // 必填验证
  if (isRequired) {
    if (valueType === '1') {
      // 枚举类型必填
      rules.push({
        required: true,
        message: `请选择${item.name}`,
        // 无论单选/多选，前端表单值统一为数组
        type: 'array',
      })
    } else if (valueType === '3') {
      // 数字类型必填
      rules.push({
        required: true,
        message: `请输入${item.name}`,
      })
    } else {
      // 文本和日期类型必填
      rules.push({
        required: true,
        message: `请输入${item.name}`,
      })
    }
  }

  // 文本类型的空白验证
  if (isTextType) {
    rules.push({
      whitespace: true,
    })
  }

  return rules
}

// 获取日期类型的值转换函数
export const getDateValueProps = () => {
  return {
    getValueProps: (value: string | moment.Moment | undefined) => {
      // 日期类型：将字符串转换为 moment 对象
      if (value && typeof value === 'string') {
        const momentValue = moment(value, 'YYYY-MM-DD')
        if (momentValue.isValid()) {
          return { value: momentValue }
        }
      }
      return { value: undefined }
    },
    getValueFromEvent: (value: moment.Moment | null | undefined) => {
      // 日期类型：将 moment 对象转换为字符串
      if (value && moment.isMoment(value)) {
        return value.format('YYYY-MM-DD')
      }
      return value
    },
  }
}

// 设置表单初始值的 Hook
export const useExpandAttrInitialValues = (
  fieldList: ExpandAttrFieldItem[],
  form: FormInstance,
  fieldKey: 'id' | 'name' = 'id',
) => {
  React.useEffect(() => {
    // 仅为“尚未有值”的字段设置初始值，避免覆盖缓存回填/接口回填的 form 值
    const fields = fieldList.map((item) =>
      fieldKey === 'id' ? item.id : item.name,
    )
    const currentValues = form.getFieldsValue(fields)

    const patchValues: Record<string, string | string[] | number | undefined> =
      {}

    fieldList.forEach((item) => {
      const field = fieldKey === 'id' ? item.id : item.name
      const current = currentValues?.[field]

      // 已经有值（包括 0、''、[] 等用户/缓存设置的值）则不覆盖
      if (current !== undefined) return

      const valueType = item.valueType || '2'
      if (valueType === '1') {
        // 枚举类型：使用 enumValueList，前端表单值统一为数组（单选长度为 0/1）
        if (item.enumValueList && item.enumValueList.length > 0) {
          patchValues[field] = [...item.enumValueList]
        } else {
          patchValues[field] = []
        }
      } else if (valueType === '3') {
        // 数字类型：将字符串转换为数字
        if (
          item.value !== undefined &&
          item.value !== null &&
          item.value !== ''
        ) {
          const numValue = Number(item.value)
          patchValues[field] = isNaN(numValue) ? undefined : numValue
        } else {
          patchValues[field] = undefined
        }
      } else {
        // 其他类型（文本、日期）：使用 value
        patchValues[field] = item.value || undefined
      }
    })

    if (Object.keys(patchValues).length > 0) {
      form.setFieldsValue(patchValues)
    }
  }, [fieldList, form])
}
import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Input,
  DatePicker,
  InputNumber,
  Spin,
  Dropdown,
  Tag,
  Space,
} from 'antd'
import { FormInstance } from 'antd/lib/form'
import moment from 'moment'
import { DownOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { PAGE_EXPAND_NAME_ENUM_LIST } from '../../api/MaterialApi'
import Style from './index.module.scss'

export interface ExpandAttrFieldItem {
  id: string
  name: string
  valueType?: string // 值类型：'1' 枚举，'2' 文本，'3' 数字，'4' 日期
  multiple?: string // 是否多选：'0' 单选，'1' 多选
  enumValueList?: string[] // 选中的枚举值列表
  allEnumValueList?: string[] // 所有枚举值列表
  value?: string // 字段值
  required?: boolean // 是否必填：false 不必填，true 必填
  platformAbbreviationName?: string // 平台要求（可选）
}

// 属性值列表项接口
interface AttributeValueItem {
  id: string
  name: string
  value: string
  code?: string
}

// 获取属性值列表的 API 函数
const getAttributeValueList = async (params: {
  fieldNameId: string // 属性项 id
  pageNo: number // 页码
  pageSize: number // 每页数量
  keyword?: string // 搜索关键词
}): Promise<{
  current: number
  total: number
  data: AttributeValueItem[]
}> => {
  const res = await PAGE_EXPAND_NAME_ENUM_LIST({
    fieldNameId: params.fieldNameId,
    pageNo: params.pageNo,
    pageSize: params.pageSize,
    value: params.keyword || '',
  })
  if (res.code === 0 && res.data) {
    return {
      current: res.data.pageNo || params.pageNo,
      total: res.data.totalCount || 0,
      data: (res.data.content || []).map((item: any) => ({
        id: item.id,
        name: item.value, // API 返回的 value 字段映射为 name
        value: item.value,
        code: item.id,
      })),
    }
  }
  return {
    current: params.pageNo,
    total: 0,
    data: [],
  }
}

interface RenderFormControlProps {
  item: ExpandAttrFieldItem
  placeholder?: string
  allReadOnly?: boolean
  readOnlyComponent?: React.ReactNode // 只读状态下的组件
  form?: FormInstance // 表单实例，用于获取校验状态
  fieldKey?: 'id' | 'name'
}

// 支持滚动加载和搜索的 Select 组件
// 约定：枚举字段后端 enumValueList 永远是数组，前端表单值也统一使用 string[]（单选长度为 0/1）
interface ScrollLoadSelectProps {
  item: ExpandAttrFieldItem
  placeholder?: string
  isMultiple: boolean
  value?: string[] // 表单值，统一使用数组
  onChange?: (value: string[] | undefined) => void // 表单值变化回调
  status?: 'error' | 'warning' // 表单校验状态
}

const ScrollLoadSelect: React.FC<ScrollLoadSelectProps> = ({
  item,
  placeholder = '请输入',
  isMultiple,
  value,
  onChange,
  status,
}) => {
  const [dataSource, setDataSource] = useState<AttributeValueItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [hasMore, setHasMore] = useState(true)
  const [visible, setVisible] = useState(false)
  const currentPageRef = useRef(1)
  const totalRef = useRef(0)
  const loadingRef = useRef(false)
  const searchTimerRef = useRef<NodeJS.Timeout>()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const pageSize = 10 // 每页数量

  const fieldId = item.id
  // 用于标记是否已经初始化过（区分初始值和用户清空后的值）
  const valueInitializedRef = useRef(false)

  // 选中的值从表单 value 获取，如果没有则从 item.enumValueList 获取（初始值）
  const selectedValues = React.useMemo(() => {
    if (value !== undefined) {
      // 只要表单里已经有值（包括 []），认为已经初始化过
      valueInitializedRef.current = true
      return value
    }
    // 如果已经初始化过，说明用户主动清空了，返回空数组
    // 否则返回初始值（来自字段配置的 enumValueList）
    if (valueInitializedRef.current) {
      return []
    }
    return item.enumValueList || []
  }, [value, item.enumValueList])

  // 加载数据
  const loadData = useCallback(
    async (page = 1, keyword = '', append = false) => {
      if (!fieldId || loadingRef.current) return

      loadingRef.current = true
      setLoading(true)

      try {
        const result = await getAttributeValueList({
          fieldNameId: fieldId,
          pageNo: page,
          pageSize: pageSize,
          keyword: keyword,
        })

        if (result.data) {
          if (append) {
            setDataSource((prev) => [...prev, ...result.data])
          } else {
            setDataSource(result.data)
          }

          totalRef.current = result.total || 0
          currentPageRef.current = result.current || page
          setHasMore((result.current || page) * pageSize < (result.total || 0))
        }
      } catch (error) {
        // 加载数据失败，静默处理
        // eslint-disable-next-line no-console
        console.error('加载数据失败:', error)
      } finally {
        setLoading(false)
        loadingRef.current = false
      }
    },
    [fieldId],
  )

  // 初始化加载
  const initializedRef = useRef(false)
  useEffect(() => {
    if (visible && !initializedRef.current) {
      if (fieldId) {
        // 如果有 fieldId，从 API 加载数据
        loadData(1, '', false)
        initializedRef.current = true
      } else if (item.allEnumValueList && item.allEnumValueList.length > 0) {
        // 如果没有 fieldId 但有静态枚举列表，使用静态数据
        const staticData: AttributeValueItem[] = item.allEnumValueList.map(
          (val: string) => ({
            id: val,
            name: val,
            value: val,
          }),
        )
        setDataSource(staticData)
        setHasMore(false)
        initializedRef.current = true
      }
    }
    if (!visible) {
      // 关闭下拉框时重置初始化状态
      initializedRef.current = false
    }
  }, [fieldId, visible, item.allEnumValueList, loadData])

  // 打开下拉框时加载数据
  const handleVisibleChange = (open: boolean) => {
    setVisible(open)
  }

  // 处理搜索
  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value)
      currentPageRef.current = 1

      // 防抖处理
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }

      searchTimerRef.current = setTimeout(() => {
        if (fieldId) {
          // 从 API 搜索
          loadData(1, value, false)
        } else if (item.allEnumValueList) {
          // 本地过滤静态枚举列表
          const filtered = item.allEnumValueList.filter((val: string) =>
            val.toLowerCase().includes(value.toLowerCase()),
          )
          const staticData: AttributeValueItem[] = filtered.map(
            (val: string) => ({
              id: val,
              name: val,
              value: val,
            }),
          )
          setDataSource(staticData)
        }
      }, 300)
    },
    [fieldId, loadData],
  )

  // 处理滚动加载
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { target } = e
      const scrollElement = target as HTMLElement
      const scrollTop = scrollElement.scrollTop
      const scrollHeight = scrollElement.scrollHeight
      const clientHeight = scrollElement.clientHeight

      // 滚动到底部附近时加载更多（仅在有 fieldId 时，静态数据不需要分页）
      if (
        scrollHeight - scrollTop - clientHeight < 50 &&
        hasMore &&
        !loading &&
        fieldId
      ) {
        loadData(currentPageRef.current + 1, searchValue, true)
      }
    },
    [hasMore, loading, fieldId, searchValue, loadData],
  )

  // 点击行选择
  const handleClickRow = useCallback(
    (record: AttributeValueItem) => {
      const recordValue = record.name

      if (isMultiple) {
        // 多选：切换选中状态
        const newSelected = selectedValues.includes(recordValue)
          ? selectedValues.filter((v) => v !== recordValue)
          : [...selectedValues, recordValue]

        onChange?.(newSelected)
      } else {
        // 单选：保存为长度为 1 的数组并关闭下拉框
        onChange?.([recordValue])
        setVisible(false)
      }
    },
    [isMultiple, selectedValues, onChange],
  )

  // 判断是否选中
  const isSelected = useCallback(
    (value: string) => {
      return selectedValues.includes(value)
    },
    [selectedValues],
  )

  // 清理定时器
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  // 渲染选中值显示
  const renderSelectedDisplay = () => {
    if (isMultiple) {
      if (selectedValues.length === 0) {
        return <span style={{ color: '#bfbfbf' }}>{placeholder}</span>
      }
      return (
        <Space wrap size={[4, 4]}>
          {selectedValues.map((val) => (
            <Tag
              key={val}
              closable
              onClose={(e) => {
                e.stopPropagation()
                const newSelected = selectedValues.filter((v) => v !== val)
                onChange?.(newSelected)
              }}
              style={{
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                position: 'relative', // 添加相对定位
              }}
            >
              <span
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={val}
              >
                {val}
              </span>
            </Tag>
          ))}
        </Space>
      )
    } else {
      return selectedValues.length > 0 ? selectedValues[0] : placeholder
    }
  }

  // 下拉面板内容
  const dropdownContent = (
    <div
      style={{
        width: '100%',
        minWidth: '200px',
        maxHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <span style={{ fontSize: '12px', margin: '8px 0 0 8px' }}>
        {isMultiple ? `已选：${selectedValues?.length}个` : ''}
      </span>
      {/* 搜索框 */}
      <div
        className={Style['dropDown-search']}
        style={{
          padding: '8px',
          borderBottom: '1px solid #d9d9d9',
        }}
      >
        <Input
          placeholder="请输入搜索关键词"
          size="small"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
        />
      </div>

      {/* 列表内容 */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: '200px',
        }}
      >
        {loading && dataSource.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="small" />
          </div>
        ) : dataSource.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            暂无数据
          </div>
        ) : (
          dataSource.map((record) => {
            const selected = isSelected(record.name)
            return (
              <div
                key={record.value || record.name}
                onClick={() => handleClickRow(record)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: selected ? '#f0f7ff' : 'transparent',
                  borderLeft: selected
                    ? '3px solid #1890ff'
                    : '3px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (!selected) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selected) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <div style={{ fontSize: '14px' }}>{record.name}</div>
              </div>
            )
          })
        )}
        {loading && dataSource.length > 0 && (
          <div style={{ textAlign: 'center', padding: '8px' }}>
            <Spin size="small" />
          </div>
        )}
      </div>
    </div>
  )

  return (
    <Dropdown
      overlay={dropdownContent}
      trigger={['click']}
      visible={visible}
      onVisibleChange={handleVisibleChange}
      getPopupContainer={(triggerNode) =>
        triggerNode.parentElement || document.body
      }
    >
      <div
        style={{
          padding: '2px 4px',
          border:
            status === 'error' ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
          cursor: 'pointer',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff',
          position: 'relative',
        }}
      >
        <div
          style={{
            maxHeight: '100px',
            overflow: 'auto',
          }}
          onClick={() => {
            setVisible(!visible)
            setSearchValue('')
          }}
        >
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {renderSelectedDisplay()}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            position: 'absolute',
            right: 4,
            top: 4,
          }}
        >
          {selectedValues.length > 0 && (
            <CloseCircleOutlined
              onClick={(e) => {
                e.stopPropagation()
                // 清空时统一置为空数组，便于必填校验依赖长度判断
                onChange?.([])
              }}
              style={{ color: '#999', fontSize: '12px' }}
            />
          )}
          <DownOutlined
            style={{
              color: '#999',
              fontSize: '8px',
              transform: visible ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </div>
      </div>
    </Dropdown>
  )
}

// 渲染不同类型的表单控件
export const renderFormControl = ({
  item,
  placeholder = '请输入',
  form,
  fieldKey = 'id',
}: RenderFormControlProps) => {
  const valueType = item.valueType || '2' // 默认为文本类型

  switch (valueType) {
    case '1': {
      // 枚举类型
      const isMultiple = item.multiple === '1'
      // 使用包装组件来接收Form.Item传递的value和onChange，并通过form获取校验状态
      const WrappedScrollLoadSelect: React.FC<{
        value?: string | string[]
        onChange?: (value: string[] | undefined) => void
      }> = (props) => {
        const { value, onChange } = props

        // 统一把表单值规范成数组（兼容历史上单选存 string 的场景）
        const normalizedValue = React.useMemo<string[] | undefined>(() => {
          if (value === undefined) return undefined
          return Array.isArray(value) ? value : [String(value)]
        }, [value])

        // 如果当前值不是数组，则同步一次到表单中，保证校验和提交时都是数组
        React.useEffect(() => {
          if (value !== undefined && !Array.isArray(value)) {
            onChange?.(normalizedValue || [])
          }
        }, [value, normalizedValue, onChange])
        // 在渲染时直接获取字段的校验错误状态
        // Form.Item会在字段值变化时重新渲染，所以这里会实时获取最新的校验状态
        // 使用 item.id 或 item.name 作为字段名（根据实际使用情况）
        // const fieldName = item.id || item.name
        const field = fieldKey === 'id' ? item.id : item.name
        const fieldError = form?.getFieldError?.(field)
        const hasError = fieldError && fieldError.length > 0
        const status = hasError ? 'error' : undefined

        return (
          <ScrollLoadSelect
            item={item}
            placeholder={placeholder}
            isMultiple={isMultiple}
            value={normalizedValue}
            onChange={onChange}
            status={status}
          />
        )
      }
      WrappedScrollLoadSelect.displayName = 'WrappedScrollLoadSelect'
      return <WrappedScrollLoadSelect />
    }

    case '2': // 文本类型
      return <Input placeholder={placeholder} maxLength={40} />

    case '3': // 数字类型
      return (
        <InputNumber
          placeholder={placeholder}
          style={{ width: '100%' }}
          min={-999999999 as number}
          max={999999999 as number}
          precision={0}
        />
      )

    case '4': // 日期类型
      return (
        <DatePicker
          placeholder={placeholder}
          style={{ width: '100%' }}
          format="YYYY-MM-DD"
          allowClear
          disabledDate={() => {
            return false
          }}
        />
      )

    default:
      return <Input placeholder={placeholder} />
  }
}

// 获取验证规则
export const getFieldRules = (item: ExpandAttrFieldItem) => {
  const valueType = item.valueType || '2'
  const isTextType = valueType === '2'
  const isRequired = item.required

  const rules: Array<{
    required?: boolean
    message?: string
    type?: 'string' | 'array' | 'number'
    whitespace?: boolean
  }> = []

  // 必填验证
  if (isRequired) {
    if (valueType === '1') {
      // 枚举类型必填
      rules.push({
        required: true,
        message: `请选择${item.name}`,
        // 无论单选/多选，前端表单值统一为数组
        type: 'array',
      })
    } else if (valueType === '3') {
      // 数字类型必填
      rules.push({
        required: true,
        message: `请输入${item.name}`,
      })
    } else {
      // 文本和日期类型必填
      rules.push({
        required: true,
        message: `请输入${item.name}`,
      })
    }
  }

  // 文本类型的空白验证
  if (isTextType) {
    rules.push({
      whitespace: true,
    })
  }

  return rules
}

// 获取日期类型的值转换函数
export const getDateValueProps = () => {
  return {
    getValueProps: (value: string | moment.Moment | undefined) => {
      // 日期类型：将字符串转换为 moment 对象
      if (value && typeof value === 'string') {
        const momentValue = moment(value, 'YYYY-MM-DD')
        if (momentValue.isValid()) {
          return { value: momentValue }
        }
      }
      return { value: undefined }
    },
    getValueFromEvent: (value: moment.Moment | null | undefined) => {
      // 日期类型：将 moment 对象转换为字符串
      if (value && moment.isMoment(value)) {
        return value.format('YYYY-MM-DD')
      }
      return value
    },
  }
}

// 设置表单初始值的 Hook
export const useExpandAttrInitialValues = (
  fieldList: ExpandAttrFieldItem[],
  form: FormInstance,
  fieldKey: 'id' | 'name' = 'id',
) => {
  React.useEffect(() => {
    // 仅为“尚未有值”的字段设置初始值，避免覆盖缓存回填/接口回填的 form 值
    const fields = fieldList.map((item) =>
      fieldKey === 'id' ? item.id : item.name,
    )
    const currentValues = form.getFieldsValue(fields)

    const patchValues: Record<string, string | string[] | number | undefined> =
      {}

    fieldList.forEach((item) => {
      const field = fieldKey === 'id' ? item.id : item.name
      const current = currentValues?.[field]

      // 已经有值（包括 0、''、[] 等用户/缓存设置的值）则不覆盖
      if (current !== undefined) return

      const valueType = item.valueType || '2'
      if (valueType === '1') {
        // 枚举类型：使用 enumValueList，前端表单值统一为数组（单选长度为 0/1）
        if (item.enumValueList && item.enumValueList.length > 0) {
          patchValues[field] = [...item.enumValueList]
        } else {
          patchValues[field] = []
        }
      } else if (valueType === '3') {
        // 数字类型：将字符串转换为数字
        if (
          item.value !== undefined &&
          item.value !== null &&
          item.value !== ''
        ) {
          const numValue = Number(item.value)
          patchValues[field] = isNaN(numValue) ? undefined : numValue
        } else {
          patchValues[field] = undefined
        }
      } else {
        // 其他类型（文本、日期）：使用 value
        patchValues[field] = item.value || undefined
      }
    })

    if (Object.keys(patchValues).length > 0) {
      form.setFieldsValue(patchValues)
    }
  }, [fieldList, form])
}
